// OperationSection — Onglet Opération complet
// Intervenants + Visite + Projet/DAL + Financement + Devis installateur + Photos
import { useState, useEffect, useRef } from "react";
import {
  Edit3, Save, X, Loader2, Banknote, CheckCircle2, XCircle, Wrench,
  Users, CalendarDays, Clock, ClipboardList, FileText, AlertTriangle, ExternalLink,
  CalendarPlus, Mail, Phone, Send
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { BlockCard } from "../../../components/BlockCard";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { API, getAuthHeaders, parseFRorISO, formatDate } from "./helpers";
import { hasDevisInstallateur } from "../../../utils/tenantProfile";
import { formatClientFullName, formatAdresse } from "../../../utils/clientFormat";
import { toast } from "sonner";
import { useConfigList, listToOptions } from "../../../hooks/useConfigList";
import { VerifyDevisDALButton } from "../../../components/dal/VerifyDevisDALButton";
import { enteteTenant } from "../../../utils/enteteTenant";

// ─── Section Header with Edit ───
function BlockHeader({ title, icon: Icon, color, editing, saving, onEdit, onSave, onCancel, verrouille, testId }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
        <Icon className="w-4 h-4" style={{ color: color || "var(--oh-hub-operation)" }} />
        {title}
      </h3>
      {!verrouille && (
        editing ? (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={onSave} disabled={saving} className="h-7 px-2" data-testid={`save-${testId}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--oh-success-text)]" /> : <Save className="w-3.5 h-3.5 text-[var(--oh-success-text)]" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 px-2">
              <X className="w-3.5 h-3.5 text-[var(--oh-error-text)]" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2" data-testid={`edit-${testId}`}>
            <Edit3 className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
        )
      )}
    </div>
  );
}

// P1.2 — pastille d'état affichée dans le résumé d'un accordéon (replié → l'état
// reste visible, donc l'info n'est jamais "masquée").
function StateBadge({ label, tone = "muted" }) {
  const tones = {
    green: { bg: "var(--oh-stat-green-bg)", c: "var(--oh-stat-green-text)", b: "var(--oh-stat-green-border)" },
    blue: { bg: "var(--oh-stat-blue-bg)", c: "var(--oh-stat-blue-text)", b: "var(--oh-stat-blue-border)" },
    amber: { bg: "var(--oh-stat-amber-bg)", c: "var(--oh-stat-amber-text)", b: "var(--oh-stat-amber-border)" },
    muted: { bg: "var(--oh-bg-surface)", c: "var(--oh-text-muted)", b: "var(--oh-border)" },
  };
  const t = tones[tone] || tones.muted;
  return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: t.bg, color: t.c, border: `1px solid ${t.b}` }}>
      {label}
    </span>
  );
}

// Card local SUPPRIMÉ → primitive partagée <BlockCard> (chantier style 2/3).
// Les blocs FK Visiteur/Installateur passent `accent` ; tous `collapsible`.
// `marginBottom={0}` car le conteneur d'onglet gère l'espacement (space-y).

function ReadField({ label, value, color }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: "var(--oh-text-muted)" }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: color || (value ? "var(--oh-text)" : "var(--oh-text-muted)") }}>{value || "—"}</p>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", placeholder, options, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide mb-1 block" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
      {options ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg text-sm border"
          style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid={testId}>
          <option value="">-</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg text-sm border"
          style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid={testId} />
      )}
    </div>
  );
}

const fmtEuro = (v) => v ? `${Number(v).toLocaleString("fr-FR")} €` : "—";

// Options de fallback si la collection metier_config_listes n'est pas
// encore chargée ou que le tenant n'a pas de seed (cf. seed
// scripts/seed/seed_config_listes_visite_chantier.py — OH-AUDIT-010 F1).
// La source canonique reste la DB ; ces fallbacks évitent l'écran blanc
// pendant le chargement initial du hook.
const STATUT_CHANTIER_FALLBACK = [
  { value: "", label: "Non démarré" },
  { value: "a_planifier", label: "À planifier" },
  { value: "planifie", label: "Planifié" },
  { value: "en_cours", label: "En cours" },
  { value: "termine", label: "Terminé" },
  { value: "annule", label: "Annulé" },
];

// ─── Upload Devis (admin) ───
function AdminUploadDevis({ dossierId, tenantId }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // CC-10 (2026-05-06) — endpoint canonique avec DocumentSyncService.
      // Avant : POST /dossiers/{id}/documents/upload (404, n'existe pas) +
      // type/subfolder ignorés. Maintenant : POST /dossiers/{id}/documents
      // avec type_document → upload atomique MongoDB + Zoho 05_TRAVAUX/.
      formData.append("type_document", "devis_installateur");
      formData.append("nom", file.name);
      const headers = {};
      const token = localStorage.getItem("session_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      Object.assign(headers, enteteTenant(tenantId));
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, {
        method: "POST", headers, credentials: "include", body: formData,
      });
      if (res.ok) {
        toast.success("Devis uploadé (MongoDB + Zoho)");
      } else {
        let detail = `${res.status}`;
        try { const j = await res.json(); detail = j.detail || detail; } catch {}
        toast.error(`Erreur upload: ${detail}`);
      }
    } catch (e) { toast.error(`Erreur réseau: ${e.message || e}`); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-3" data-testid="admin-upload-devis">
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={handleUpload} />
      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
        data-testid="btn-admin-upload-devis"
        title="Upload PDF du devis installateur (PDF/JPG/PNG/DOC) — stocké Zoho 02_PIECES_DU_DOSSIER"
        style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}>
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <FileText className="w-3.5 h-3.5 mr-2" />}
        {uploading ? "Envoi..." : "Déposer mon devis"}
      </Button>
    </div>
  );
}

export function OperationSection({ dossier, dossierId, tenantId, isTEF, abandoned, onUpdate, children }) {
  const verrouille = dossier.verrouille || abandoned;
  const headers = getAuthHeaders(tenantId, "application/json");

  // DAL data
  const [dalData, setDalData] = useState(null);
  const [dalLoading, setDalLoading] = useState(false);

  useEffect(() => {
    if (!isTEF || !dossierId) return;
    const loadDAL = async () => {
      setDalLoading(true);
      try {
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, {
          headers: getAuthHeaders(tenantId), credentials: "include",
        });
        if (res.ok) setDalData(await res.json());
      } catch { /* silent */ }
      finally { setDalLoading(false); }
    };
    loadDAL();
  }, [dossierId, isTEF, tenantId]);

  // Generic PATCH helper
  const patchDossier = async (payload) => {
    const res = await fetch(`${API}/api/metier/dossiers/${dossier.id}`, {
      method: "PATCH", headers, credentials: "include", body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Erreur sauvegarde");
    }
    return res.json();
  };

  // ─── 1. Intervenants ───
  // Item 6 (2026-05-10) : ancien intervForm/editInterv supprimé (dead code,
  // jamais rendu). Édition des intervenants désormais répartie :
  //   - Visiteur AMO → modal "Planifier visite" (Phase A, Select op_visiteurs)
  //   - Installateur → bloc édition inline (Phase B, Select op_installateurs)

  // ─── 2. Visite ───
  // NOTE: l'édition inline (crayon) a été supprimée — toutes les modifs de visite
  // passent désormais par le Dialog "Planifier / Modifier la visite" qui synchronise GCal.

  // ─── Modal Planifier / Modifier Visite ───
  const [showPlanifModal, setShowPlanifModal] = useState(false);
  const [planifMode, setPlanifMode] = useState("create"); // "create" | "edit"
  const [planifLoading, setPlanifLoading] = useState(false);
  const [visiteursList, setVisiteursList] = useState([]);
  const [planifForm, setPlanifForm] = useState({
    titre: "", visiteur_id: "", visiteur_nom: "", visiteur_email: "", visiteur_telephone: "",
    date: "", heure_debut: "09:00", heure_fin: "11:00",
    adresse: "", description: "", client_email: "",
    notify_email: true, notify_sms: false,
  });

  // Item 6 Phase A — endpoint canonique avec _id exposé. Liste des
  // visiteurs sous forme [{id, label, prenom, nom, email}] pour stocker
  // visiteur_id (FK) au save.
  useEffect(() => {
    const loadVisiteurs = async () => {
      try {
        const res = await fetch(`${API}/api/metier/lookup/op-visiteurs`, {
          headers: { ...getAuthHeaders(tenantId), ...enteteTenant(tenantId) },
          credentials: "include",
        });
        if (res.ok) setVisiteursList(await res.json());
      } catch { /* silent */ }
    };
    loadVisiteurs();
  }, [tenantId]);

  // Item 6 Phase B — installateurs op_installateurs avec _id exposé.
  // Format [{id, label, nom, entreprise, siret, email}]. Le `label` est
  // `entreprise || nom` (déjà construit côté backend).
  const [installateursList, setInstallateursList] = useState([]);
  const [editingInstallateur, setEditingInstallateur] = useState(false);
  const [savingInstallateur, setSavingInstallateur] = useState(false);
  const [installateurForm, setInstallateurForm] = useState({ id: "", nom: "" });

  useEffect(() => {
    const loadInstallateurs = async () => {
      try {
        const res = await fetch(`${API}/api/metier/lookup/op-installateurs`, {
          headers: { ...getAuthHeaders(tenantId), ...enteteTenant(tenantId) },
          credentials: "include",
        });
        if (res.ok) setInstallateursList(await res.json());
      } catch { /* silent */ }
    };
    loadInstallateurs();
  }, [tenantId]);

  const startEditInstallateur = () => {
    // Pré-sélection : id si présent, sinon fuzzy match label
    const matched = dossier.installateur_id
      ? installateursList.find(i => i.id === dossier.installateur_id)
      : installateursList.find(i => (i.label || "").toLowerCase() === (dossier.installateur_nom || "").toLowerCase());
    setInstallateurForm({
      id: matched?.id || "",
      nom: matched?.label || dossier.installateur_nom || "",
    });
    setEditingInstallateur(true);
  };

  const saveInstallateur = async () => {
    setSavingInstallateur(true);
    try {
      const isNone = !installateurForm.id || installateurForm.id === "none";
      await patchDossier({
        installateur_id: isNone ? null : installateurForm.id,
        installateur_nom: isNone ? null : installateurForm.nom,
      });
      toast.success("Installateur mis à jour");
      setEditingInstallateur(false);
      onUpdate?.();
    } catch (e) { toast.error(e.message); }
    finally { setSavingInstallateur(false); }
  };

  // ─── Visiteur (bloc dédié éditable — P1.2/#4) ───
  // Régression corrigée : la sélection du visiteur n'était possible que via le
  // modal « Planifier visite ». On expose un sélecteur FK op_visiteurs autonome
  // (même source que le modal : visiteursList), persisté direct via PATCH dossier.
  const [editingVisiteur, setEditingVisiteur] = useState(false);
  const [savingVisiteur, setSavingVisiteur] = useState(false);
  const [visiteurForm, setVisiteurForm] = useState({ id: "", nom: "" });

  const startEditVisiteur = () => {
    const matched = dossier.visiteur_id
      ? visiteursList.find(v => v.id === dossier.visiteur_id)
      : visiteursList.find(v => (v.label || "").toLowerCase() === (dossier.visiteur_amo || "").toLowerCase());
    setVisiteurForm({
      id: matched?.id || "",
      nom: matched?.label || dossier.visiteur_amo || "",
    });
    setEditingVisiteur(true);
  };

  const saveVisiteur = async () => {
    setSavingVisiteur(true);
    try {
      const v = visiteursList.find(x => x.id === visiteurForm.id);
      const isNone = !visiteurForm.id || visiteurForm.id === "none";
      await patchDossier({
        visiteur_id: isNone ? null : visiteurForm.id,
        visiteur_amo: isNone ? null : (v?.label || visiteurForm.nom || null),
        visiteur_email: isNone ? null : (v?.email || null),
        visiteur_telephone: isNone ? null : (v?.telephone || null),
      });
      toast.success("Visiteur mis à jour");
      setEditingVisiteur(false);
      onUpdate?.();
    } catch (e) { toast.error(e.message); }
    finally { setSavingVisiteur(false); }
  };

  // Téléphone visiteur courant (depuis le dossier, repli sur la fiche op_visiteurs).
  const currentVisiteurTel = dossier.visiteur_telephone
    || (visiteursList.find(v => v.id === dossier.visiteur_id)?.telephone)
    || null;

  // Téléphone installateur courant (FK op_installateurs ; mockup : nom · tél).
  const currentInstallateurTel = (() => {
    const m = dossier.installateur_id
      ? installateursList.find(i => i.id === dossier.installateur_id)
      : installateursList.find(i => (i.label || "").toLowerCase() === (dossier.installateur_nom || "").toLowerCase());
    return m?.telephone || null;
  })();

  const handleSelectVisiteur = (id) => {
    if (!id) {
      setPlanifForm(f => ({
        ...f, visiteur_id: "", visiteur_nom: "", visiteur_email: "", visiteur_telephone: "",
      }));
      return;
    }
    const v = visiteursList.find(x => x.id === id);
    setPlanifForm(f => ({
      ...f,
      visiteur_id: id,
      visiteur_nom: v?.label || "",
      visiteur_email: v?.email || "",
      visiteur_telephone: v?.telephone || "",
    }));
  };

  const handlePlanifierVisite = async () => {
    if (!planifForm.visiteur_nom || !planifForm.date) {
      toast.error("Visiteur et date requis");
      return;
    }
    setPlanifLoading(true);
    try {
      let notifyMode = "email";
      if (planifForm.notify_email && planifForm.notify_sms) notifyMode = "both";
      else if (planifForm.notify_sms) notifyMode = "sms";
      else if (planifForm.notify_email) notifyMode = "email";

      // 🔴 N°746 point 7 — CHEMIN NEUTRE. Ce bloc n'est gaté par RIEN : il est servi à
      // `mario_bat` comme à `tef`. L'ancien chemin portait `/tef/` dans l'URL — un peintre
      // planifiait donc sa visite par une route TEF. ⛔ Ne pas remettre `/tef/` ici.
      // ⚠️ La route neutre DÉLÈGUE à la même implémentation : ⛔ aucun comportement ne change
      //    pour TEF (mêmes champs, même événement Google, mêmes notifications).
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/planifier-visite`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          visiteur_id: planifForm.visiteur_id || null,
          visiteur_nom: planifForm.visiteur_nom,
          visiteur_email: planifForm.visiteur_email,
          visiteur_telephone: planifForm.visiteur_telephone,
          date: planifForm.date,
          heure_debut: planifForm.heure_debut,
          heure_fin: planifForm.heure_fin,
          titre: planifForm.titre || null,
          adresse: planifForm.adresse || null,
          description: planifForm.description || null,
          client_email: planifForm.client_email || null,
          notify_visiteur_mode: notifyMode,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Visite planifiée");
        await onUpdate?.();
        setShowPlanifModal(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setPlanifLoading(false); }
  };

  // Détermine le vrai statut visite avec logique contextuelle
  // Règle #5 : ne plus passer à "effectue" juste parce qu'une date_visite existe.
  // Une visite est "Effectuée" UNIQUEMENT si statut_rdv l'affirme explicitement,
  // OU si la date+heure_fin est passée depuis +2h (grace period) → "À confirmer".
  const effectiveVisiteStatut = (() => {
    if (dossier.statut_rdv === "annule") return "annule";
    if (dossier.statut_rdv === "effectue") return "effectue";
    if (dossier.date_visite) {
      // Visite dans le futur (ou en cours) → "planifie" ou "en_cours"
      const endDate = parseFRorISO(dossier.date_visite);
      if (endDate) {
        const [hh, mm] = (dossier.heure_fin_visite || dossier.heure_visite || "23:59").split(":");
        endDate.setHours(parseInt(hh, 10) || 23, parseInt(mm, 10) || 59, 0, 0);
        const endMs = endDate.getTime();
        const nowMs = Date.now();
        const GRACE_MS = 2 * 60 * 60 * 1000; // 2h après heure_fin
        if (nowMs < endMs) return dossier.statut_rdv === "confirme" ? "confirme" : "planifie";
        if (nowMs >= endMs + GRACE_MS) return "a_confirmer"; // passée depuis +2h, pas marquée effectuée
        return "en_cours"; // dans la fenêtre [end, end+2h]
      }
      // date_visite présent mais non parsable : fallback
      return dossier.statut_rdv || "planifie";
    }
    if (dossier.statut_rdv === "planifie" || dossier.statut_rdv === "confirme") return dossier.statut_rdv;
    if (dossier.visiteur_amo && !dossier.statut_rdv) return "visiteur_sans_rdv";
    return "";
  })();
  const visiteLabels = {
    effectue: "Effectuée",
    planifie: "Planifiée",
    confirme: "Confirmée",
    en_cours: "En cours",
    a_confirmer: "À confirmer",
    annule: "Annulée",
    visiteur_sans_rdv: "Visiteur assigné, RDV à planifier",
    "": "Non planifiée",
  };
  const visiteLabel = visiteLabels[effectiveVisiteStatut] || "Non planifiée";
  const visiteColor = effectiveVisiteStatut === "effectue" ? "#10b981"
    : effectiveVisiteStatut === "planifie" || effectiveVisiteStatut === "confirme" ? "var(--oh-stat-blue-text)"
    : effectiveVisiteStatut === "en_cours" ? "#8b5cf6"
    : effectiveVisiteStatut === "a_confirmer" ? "var(--oh-stat-amber-text)"
    : effectiveVisiteStatut === "annule" ? "var(--oh-stat-red-text)"
    : effectiveVisiteStatut === "visiteur_sans_rdv" ? "var(--oh-stat-amber-text)"
    : "var(--oh-stat-slate-text)";

  // Radio group 3 états : Non planifiée / Planifiée / Effectuée
  // - Non planifiée : statut_rdv=null ET date_visite=null  → reset complet
  // - Planifiée     : statut_rdv="planifie" (date_visite conservée si présente)
  // - Effectuée     : statut_rdv="effectue" (écrase la logique auto-compute)
  const [savingStatut, setSavingStatut] = useState(false);
  const changeStatutVisite = async (nouveauStatut) => {
    if (savingStatut) return;
    setSavingStatut(true);
    try {
      const payload = { statut_rdv: nouveauStatut === "non_planifie" ? null : nouveauStatut };
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: "include",
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const labels = { non_planifie: "Non planifiée", planifie: "Planifiée", effectue: "Effectuée" };
        toast.success(`Visite : ${labels[nouveauStatut]}`);
        onUpdate?.();
      } else toast.error("Erreur");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingStatut(false); }
  };
  // État actuel à cocher dans le radio (lecture seule = calcul à partir de statut_rdv + date_visite)
  const radioVisiteValue = (() => {
    if (dossier.statut_rdv === "effectue") return "effectue";
    if (dossier.date_visite || dossier.statut_rdv === "planifie" || dossier.statut_rdv === "confirme") return "planifie";
    return "non_planifie";
  })();

  // ─── 3. Projet / Travaux ───
  const [editProjet, setEditProjet] = useState(false);
  const [savingProjet, setSavingProjet] = useState(false);
  const [projetForm, setProjetForm] = useState({});

  const startEditProjet = () => {
    setProjetForm({
      type_projet: dossier.type_projet || "",
      description_projet: dossier.description_projet || "",
      statut_chantier: dossier.statut_chantier || "",
    });
    setEditProjet(true);
  };

  const saveProjet = async () => {
    setSavingProjet(true);
    try {
      await patchDossier({
        type_projet: projetForm.type_projet || null,
        description_projet: projetForm.description_projet || null,
        statut_chantier: projetForm.statut_chantier || null,
      });
      toast.success("Projet mis à jour");
      setEditProjet(false);
      onUpdate?.();
    } catch (e) { toast.error(e.message); }
    finally { setSavingProjet(false); }
  };

  // DAL coherence check
  const dalZones = dalData?.zones_evaluees || [];
  const dalHasContent = dalZones.length > 0 || dalData?.observations;
  const devisMontant = dossier.montant_devis;
  const coherenceDAL = dalHasContent && devisMontant ? "coherent" : dalHasContent && !devisMontant ? "ecart" : !dalHasContent ? "dal_manquant" : "ok";

  // Statuts chantier : source canonique = metier_config_listes (DB-driven,
  // tenant-scoped). Fallback hardcoded si DB pas encore chargée.
  const { items: chantierItems } = useConfigList("statuts_chantier");
  // P1.2 — source_apport externalisé vers metier_config_listes (fallback = valeurs en dur).
  const { items: sourceApportItems } = useConfigList("source_apport");
  const statutChantierOptions = chantierItems.length
    ? listToOptions(chantierItems)
    : STATUT_CHANTIER_FALLBACK;
  const chantierLabel = statutChantierOptions.find(o => o.value === (dossier.statut_chantier || ""))?.label || "Non démarré";

  // ─── 4. Financement ───
  const [editFin, setEditFin] = useState(false);
  const [savingFin, setSavingFin] = useState(false);
  const [finForm, setFinForm] = useState({});

  const startEditFin = () => {
    setFinForm({
      montant_devis: dossier.montant_devis || "",
      montant_aides: dossier.montant_aides || dossier.montant_prime || "",
      reste_a_charge: dossier.reste_a_charge || "",
    });
    setEditFin(true);
  };

  const saveFin = async () => {
    setSavingFin(true);
    try {
      const payload = {};
      if (finForm.montant_devis !== "") payload.montant_devis = parseFloat(finForm.montant_devis) || null;
      if (finForm.montant_aides !== "") payload.montant_aides = parseFloat(finForm.montant_aides) || null;
      if (finForm.reste_a_charge !== "") payload.reste_a_charge = parseFloat(finForm.reste_a_charge) || null;
      await patchDossier(payload);
      toast.success("Financement mis à jour");
      setEditFin(false);
      onUpdate?.();
    } catch (e) { toast.error(e.message); }
    finally { setSavingFin(false); }
  };

  return (
    <>
      {/* ─── VISITEUR (bloc dédié éditable — FK op_visiteurs, P1.2/#4) ─── */}
      <div data-subnav-id="visiteur">
        <BlockCard collapsible marginBottom={0} testId="bloc-visiteur" title="Visiteur" icon={Users} accent badge={
          <span className="flex items-center gap-1.5" data-testid="badge-visiteur">
            <StateBadge label={dossier.visiteur_amo || "Non assigné"} tone={dossier.visiteur_amo ? "blue" : "muted"} />
          </span>
        }>
          {/* Compact (mockup) : 1 ligne contact + édition inline, pas de grosses colonnes. */}
          {editingVisiteur ? (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={visiteurForm.id || ""}
                onChange={(e) => {
                  const v = visiteursList.find(x => x.id === e.target.value);
                  setVisiteurForm({ id: e.target.value, nom: v?.label || "" });
                }}
                className="rounded-md px-2 py-1.5 text-sm min-w-[200px]"
                style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="visiteur-select"
              >
                <option value="">Aucun</option>
                {visiteursList.map((v) => (<option key={v.id} value={v.id}>{v.label}</option>))}
              </select>
              <Button size="sm" onClick={saveVisiteur} disabled={savingVisiteur} className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-visiteur">{savingVisiteur ? "..." : "Sauver"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingVisiteur(false)} className="h-7 px-3 text-xs">Annuler</Button>
            </div>
          ) : (
            <div className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium" style={{ color: dossier.visiteur_amo ? "var(--oh-text)" : "var(--oh-text-muted)" }} data-testid="visiteur-nom">{dossier.visiteur_amo || "Non assigné"}</span>
              {currentVisiteurTel && (<><span style={{ color: "var(--oh-text-muted)" }}>·</span><a href={`tel:${currentVisiteurTel}`} className="flex items-center gap-1 text-[var(--oh-accent-text)] hover:underline" data-testid="visiteur-tel"><Phone className="w-3 h-3" /> {currentVisiteurTel}</a></>)}
              {dossier.visiteur_email && (<><span style={{ color: "var(--oh-text-muted)" }}>·</span><a href={`mailto:${dossier.visiteur_email}`} className="flex items-center gap-1 text-[var(--oh-accent-text)] hover:underline" data-testid="visiteur-email"><Mail className="w-3 h-3" /> {dossier.visiteur_email}</a></>)}
              {!verrouille && (<Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-auto" onClick={startEditVisiteur} data-testid="btn-edit-visiteur"><Edit3 className="w-3 h-3 mr-1" /> Modifier</Button>)}
            </div>
          )}
          <p className="text-[11px] mt-2" style={{ color: "var(--oh-text-muted)", fontStyle: "italic" }}>
            Source FK op_visiteurs — choix/change ICI ; en Infos = lecture seule. Planification RDV dans le bloc <strong>Visite</strong>.
          </p>
        </BlockCard>
      </div>

      {/* ─── 3. INSTALLATEUR (Item 6 Phase B — édition Select op_installateurs) ───
          🔴🔴 N°690-2 — GATÉ PAR LA CAPACITÉ DÉCLARÉE `devis_installateur_requis`.
          ⚠️ MESURÉ : ce bloc de SOUS-TRAITANCE ⛔ n'était gaté par RIEN. Un
          artisan qui POSE LUI-MÊME ⛔ n'a PERSONNE à assigner — et il avait
          sous les yeux « installateur », « devis installateur », « montant
          travaux ».
          🔑 La capacité existait EN BASE depuis la migration 058 (tef,
          omv_france, climatpac) et ⛔ n'était servie à PERSONNE : je l'ai
          déclarée au modèle, servie par `/platform/user/tenants`, rangée par
          OmniHub. ⛔ Aucun slug, ⛔ aucun isTEF. Absente ⇒ ⛔ pas de bloc. */}
      {hasDevisInstallateur() && (
      <div data-subnav-id="installateur">
        <BlockCard collapsible marginBottom={0} testId="bloc-installateur" title="Installateur" icon={Wrench} accent badge={
          <span className="flex items-center gap-1.5" data-testid="badge-installateur">
            <StateBadge label={dossier.installateur_nom || "Non assigné"} tone={dossier.installateur_nom ? "blue" : "muted"} />
            {dossier.montant_devis ? <StateBadge label="Devis reçu" tone="green" /> : null}
          </span>
        }>
          {/* Badge source d'apport */}
          <div className="mb-3 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>
              Source apport client :
            </span>
            {dossier.source_apport === "direct" ? (
              <Badge style={{ background: "rgba(16,185,129,0.12)", color: "#10b981",
                              border: "1px solid rgba(16,185,129,0.3)" }}
                     data-testid="badge-source-direct">
                Apport direct
              </Badge>
            ) : dossier.source_apport === "installateur" ? (
              <Badge style={{ background: "rgba(59,130,246,0.12)", color: "#3b82f6",
                              border: "1px solid rgba(59,130,246,0.3)" }}
                     data-testid="badge-source-installateur">
                Via installateur
              </Badge>
            ) : (
              <Badge style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text-muted)" }}
                     data-testid="badge-source-na">
                À renseigner
              </Badge>
            )}
            {!editingInstallateur && (
              <select
                value={dossier.source_apport || ""}
                onChange={async (e) => {
                  try {
                    await patchDossier({ source_apport: e.target.value || null });
                    onUpdate?.();
                  } catch (err) { toast.error(err.message); }
                }}
                className="text-xs rounded px-2 py-0.5"
                style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="select-source-apport"
                aria-label="Source apport client"
              >
                <option value="">— non défini —</option>
                {(sourceApportItems && sourceApportItems.length
                  ? sourceApportItems
                  : [{ id: "direct", label: "Apport direct" }, { id: "installateur", label: "Via installateur" }]
                ).filter(o => o.id).map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            )}
          </div>
          {/* Compact (mockup) : ligne contact nom · tél · montant · statut. */}
          {editingInstallateur ? (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={installateurForm.id || ""}
                onChange={(e) => {
                  const v = installateursList.find(x => x.id === e.target.value);
                  setInstallateurForm({ id: e.target.value, nom: v?.label || "" });
                }}
                className="rounded-md px-2 py-1.5 text-sm min-w-[200px]"
                style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="installateur-select"
              >
                <option value="">Aucun</option>
                {installateursList.map((i) => (<option key={i.id} value={i.id}>{i.label}</option>))}
              </select>
              <Button size="sm" onClick={saveInstallateur} disabled={savingInstallateur} className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-installateur">{savingInstallateur ? "..." : "Sauver"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingInstallateur(false)} className="h-7 px-3 text-xs">Annuler</Button>
            </div>
          ) : (
            <div className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium" style={{ color: dossier.installateur_nom ? "var(--oh-text)" : "var(--oh-text-muted)" }} data-testid="installateur-nom">{dossier.installateur_nom || "Non assigné"}</span>
              {currentInstallateurTel && (<><span style={{ color: "var(--oh-text-muted)" }}>·</span><a href={`tel:${currentInstallateurTel}`} className="flex items-center gap-1 text-[var(--oh-accent-text)] hover:underline" data-testid="installateur-tel"><Phone className="w-3 h-3" /> {currentInstallateurTel}</a></>)}
              <span style={{ color: "var(--oh-text-muted)" }}>· Montant devis : {fmtEuro(dossier.montant_devis)}</span>
              <Badge className="text-xs border-0" style={{ background: dossier.montant_devis ? "var(--oh-stat-green-bg)" : "var(--oh-stat-slate-bg)", color: dossier.montant_devis ? "var(--oh-stat-green-text)" : "var(--oh-stat-slate-text)" }}>{dossier.montant_devis ? "Devis reçu" : "En attente"}</Badge>
              {!verrouille && dossier.source_apport !== "direct" && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-auto" onClick={startEditInstallateur} data-testid="btn-edit-installateur">
                  <Edit3 className="w-3 h-3 mr-1" /> Modifier
                </Button>
              )}
            </div>
          )}
          {/* Rappel cohérence DAL */}
          {isTEF && coherenceDAL === "ecart" && (
            <p className="text-xs mt-2 flex items-center gap-1" style={{ color: "var(--oh-stat-amber-text)" }}>
              <AlertTriangle className="w-3 h-3" /> Le devis doit être aligné avec le DAL
            </p>
          )}
          {/* Bouton Vérification Devis vs DAL — seulement si DAL complété */}
          {isTEF && dalData?.dal_genere && (
            <div className="mt-3">
              <VerifyDevisDALButton
                dossierId={dossierId}
                tenantId={tenantId}
                hasDevis={!!dossier.montant_devis || (dossier.devis_ids && dossier.devis_ids.length > 0)}
              />
            </div>
          )}
          {/* Upload devis (admin) */}
          <AdminUploadDevis dossierId={dossierId} tenantId={tenantId} />
        </BlockCard>
      </div>
      )}

      {/* ─── 1. VISITE ─── (le PLANNING du RDV, ⛔ pas le relevé terrain)
          🔴🔴 N°705-7 — GATÉ PAR `devis_installateur_requis`.
          ⚠️ MESURÉ chez Mario : date de visite, statut_rdv, Google Calendar —
          TOUS ces champs sont VIDES. C'est le planning d'un intervenant qu'on
          ENVOIE ; Mario fait SES visites lui-même par l'application terrain.
          ⛔ CE ⛔ N'EST PAS le panneau « Dossier ▸ Visites », qui FONCTIONNE et
          que je ⛔ ne TOUCHE PAS : deux objets distincts.
          🔑 tef, omv_france et climatpac DÉCLARENT la capacité ⇒ bloc
          INCHANGÉ pour eux. */}
      {hasDevisInstallateur() && (
      <div data-subnav-id="visite">
        <BlockCard collapsible marginBottom={0} iconColor="var(--oh-hub-operation)" testId="bloc-visite" title="Visite" icon={CalendarDays} badge={
          <span className="flex items-center gap-1.5" data-testid="badge-visite">
            <StateBadge
              label={dossier.statut_rdv === "effectue" ? "Effectuée" : (dossier.date_visite ? "Planifiée" : "Non planifiée")}
              tone={dossier.statut_rdv === "effectue" ? "green" : (dossier.date_visite ? "blue" : "muted")}
            />
            {dossier.visite_payee && <StateBadge label="Payée" tone="green" />}
          </span>
        }>
          {!verrouille && (
            <div className="flex justify-end gap-2 mb-3">
              {!dossier.date_visite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    const clientNom = formatClientFullName(dossier.client) || dossier.client_nom || "Client";
                    // Fix [object Object] (TEF-DOS-2026-00257) : logement.adresse
                    // peut être une string (rue seule, majorité des dossiers) OU
                    // un objet {rue, code_postal, ville, complement}. Sans
                    // discrimination, [...].join() rend "[object Object]".
                    const log = dossier.logement || {};
                    const logAdr = log.adresse;
                    const isLogObj = logAdr && typeof logAdr === "object";
                    const logRue = isLogObj ? logAdr.rue : (typeof logAdr === "string" ? logAdr : "");
                    const logCp = (isLogObj && logAdr.code_postal) || log.code_postal || "";
                    const logVille = (isLogObj && logAdr.ville) || log.ville || "";
                    // Fallback : client.adresse (objet ou string)
                    const cli = dossier.client || {};
                    const cliAdr = cli.adresse;
                    const isCliObj = cliAdr && typeof cliAdr === "object";
                    const cliRue = cli.adresse_rue || (isCliObj ? cliAdr.rue : (typeof cliAdr === "string" ? cliAdr : null));
                    const cliCp = cli.code_postal || (isCliObj ? cliAdr.code_postal : null);
                    const cliVille = cli.ville || (isCliObj ? cliAdr.ville : null);
                    const rue = logRue || cliRue;
                    const cp = logCp || cliCp;
                    const ville = logVille || cliVille;
                    const adresseComplete = [rue, [cp, ville].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                    setPlanifMode("create");
                    // Pré-sélection : si visiteur_id présent → utiliser, sinon
                    // fuzzy match par visiteur_amo (nom complet) sur visiteursList.
                    const matchedV = dossier.visiteur_id
                      ? visiteursList.find(v => v.id === dossier.visiteur_id)
                      : visiteursList.find(v => (v.label || "").toLowerCase() === (dossier.visiteur_amo || "").toLowerCase());
                    setPlanifForm(f => ({
                      ...f,
                      // 🔴🔴 N°752 — « Visite AMO » ÉTAIT ÉCRIT DANS L'AGENDA DU
                      // CLIENT. ⚠️ Relevé par CC AUDIT sur mon fichier (l.775, 828,
                      // 1034, 1182) : ce titre part dans Google Calendar, ⛔ sans
                      // aucun gate — un peintre écrivait donc « Visite AMO » dans
                      // SON agenda, et ça RESTE en donnée, ⛔ pas seulement à
                      // l'écran. 🔑 On applique la règle que JJ a posée pour le même
                      // objet au N°737 : « le bloc n'est pas AMO, c'est le NOM qui
                      // l'était. ⛔ On ne le gate pas, on le RENOMME » — donc pour
                      // TOUS les tenants, ⛔ sans dérivation par capacité.
                      titre: `Visite — ${clientNom}`,
                      visiteur_id: matchedV?.id || "",
                      visiteur_nom: matchedV?.label || dossier.visiteur_amo || "",
                      visiteur_email: matchedV?.email || dossier.visiteur_email || "",
                      visiteur_telephone: dossier.visiteur_telephone || "",
                      date: "",
                      heure_debut: "09:00",
                      heure_fin: "11:00",
                      adresse: adresseComplete,
                      description: "",
                      client_email: dossier.client?.email || "",
                    }));
                    setShowPlanifModal(true);
                  }}
                  className="h-8 px-4 text-sm font-medium bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] shadow-sm"
                  data-testid="btn-planifier-visite"
                >
                  <CalendarPlus className="w-4 h-4 mr-1.5" /> Planifier la visite
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const clientNom = formatClientFullName(dossier.client) || dossier.client_nom || "Client";
                    // Priorité : visite_adresse (déjà saisie) → logement → client.
                    // Fix [object Object] : log.adresse peut être objet
                    // {rue, code_postal, ville} (cf. TEF-DOS-2026-00257).
                    const log = dossier.logement || {};
                    const logAdr = log.adresse;
                    const isLogObj = logAdr && typeof logAdr === "object";
                    const logRue = isLogObj ? logAdr.rue : (typeof logAdr === "string" ? logAdr : "");
                    const cli = dossier.client || {};
                    const cliAdr = cli.adresse;
                    const isCliObj = cliAdr && typeof cliAdr === "object";
                    const rue = logRue || cli.adresse_rue || (isCliObj ? cliAdr.rue : (typeof cliAdr === "string" ? cliAdr : null));
                    const cp = (isLogObj && logAdr.code_postal) || log.code_postal || cli.code_postal || (isCliObj ? cliAdr.code_postal : null);
                    const ville = (isLogObj && logAdr.ville) || log.ville || cli.ville || (isCliObj ? cliAdr.ville : null);
                    const adresseFallback = [rue, [cp, ville].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                    // Si visite_adresse est un objet, l'aplatir en string avant
                    // injection dans le form (même logique que le bloc
                    // d'affichage plus bas).
                    const _va = dossier.visite_adresse;
                    const _vaObj = _va && typeof _va === "object";
                    const visiteAdresseStr = _vaObj
                      ? [_va.rue, [_va.code_postal, _va.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ")
                      : (typeof _va === "string" ? _va : "");
                    setPlanifMode("edit");
                    // Pré-sélection FK ou fuzzy nom (Item 6 Phase A)
                    const matchedV = dossier.visiteur_id
                      ? visiteursList.find(v => v.id === dossier.visiteur_id)
                      : visiteursList.find(v => (v.label || "").toLowerCase() === (dossier.visiteur_amo || "").toLowerCase());
                    setPlanifForm({
                      // ⚠️ Un titre DÉJÀ enregistré (`visite_titre`) ⛔ n'est PAS
                      // réécrit : ce lot change le DÉFAUT, ⛔ pas l'historique.
                      titre: dossier.visite_titre || `Visite — ${clientNom}`,
                      visiteur_id: matchedV?.id || "",
                      visiteur_nom: matchedV?.label || dossier.visiteur_amo || "",
                      visiteur_email: matchedV?.email || dossier.visiteur_email || "",
                      visiteur_telephone: dossier.visiteur_telephone || "",
                      date: dossier.date_visite || "",
                      heure_debut: dossier.heure_visite || "09:00",
                      heure_fin: dossier.heure_fin_visite || "11:00",
                      adresse: visiteAdresseStr || adresseFallback,
                      description: dossier.visite_description || "",
                      client_email: dossier.client?.email || "",
                      notify_email: true,
                      notify_sms: false,
                    });
                    setShowPlanifModal(true);
                  }}
                  className="h-8 px-3 text-xs"
                  style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}
                  data-testid="btn-modifier-visite"
                >
                  <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Modifier la visite
                </Button>
              )}
            </div>
          )}
          {(() => {
            // Bloc d'affichage enrichi (heure, durée, adresse, contact, lien GCal)
            const clientTel = dossier.client?.telephone || dossier.client_tel || dossier.telephone;
            const clientMobile = dossier.client?.mobile || dossier.mobile;
            const clientEmail = dossier.client?.email;
            // Fix [object Object] : client.adresse peut être soit un string, soit un objet {rue, code_postal, ville, complement}
            const adr = dossier.client?.adresse;
            const isAdrObj = adr && typeof adr === "object";
            const adresseRue = dossier.client?.adresse_rue || (isAdrObj ? adr.rue : (typeof adr === "string" ? adr : null));
            const adresseCp = dossier.client?.code_postal || (isAdrObj ? adr.code_postal : null);
            const adresseVille = dossier.client?.ville || (isAdrObj ? adr.ville : null);
            const adresseComplement = isAdrObj ? adr.complement : null;
            // P1.2 — normalisation via le helper canonique `formatAdresse` (source unique).
            // Gère string ET objet {rue, code_postal, ville, complement} et ne laisse
            // jamais fuiter "[object Object]" (fix historique TEF-DOS-2026-00251 Carvalho).
            const visiteAdrAsString = formatAdresse(dossier.visite_adresse) || null;
            const adresseVisite = visiteAdrAsString
              || [adresseRue, [adresseCp, adresseVille].filter(Boolean).join(" ")].filter(Boolean).join(", ")
              || null;
            const adresseMapsUrl = adresseVisite
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresseVisite)}`
              : null;
            const computeDuree = () => {
              if (!dossier.heure_visite || !dossier.heure_fin_visite) return null;
              try {
                const [h1, m1] = dossier.heure_visite.split(":").map(Number);
                const [h2, m2] = dossier.heure_fin_visite.split(":").map(Number);
                const mins = (h2 * 60 + m2) - (h1 * 60 + m1);
                if (mins <= 0) return null;
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                return h > 0 ? (m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`) : `${m}min`;
              } catch { return null; }
            };
            const duree = computeDuree();

            return (
              <div className="space-y-3">
                {/* Compact (mockup) : 1 ligne inline date · heure · durée · visiteur · statut RDV. */}
                <div className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="flex items-center gap-1" style={{ color: dossier.date_visite ? "var(--oh-text)" : "var(--oh-text-muted)" }} data-testid="visite-date">
                    <CalendarDays className="w-3.5 h-3.5" style={{ color: dossier.date_visite ? "#10b981" : "var(--oh-text-muted)" }} />
                    {(() => { const d = parseFRorISO(dossier.date_visite); return d ? d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "Non planifiée"; })()}
                  </span>
                  {dossier.heure_visite && (
                    <>
                      <span style={{ color: "var(--oh-text-muted)" }}>·</span>
                      <span className="flex items-center gap-1" data-testid="visite-heure"><Clock className="w-3.5 h-3.5" style={{ color: "var(--oh-stat-blue-text)" }} />{dossier.heure_visite}{dossier.heure_fin_visite && <> → {dossier.heure_fin_visite}</>}{duree && <span style={{ color: "var(--oh-text-muted)" }}> ({duree})</span>}</span>
                    </>
                  )}
                  <span style={{ color: "var(--oh-text-muted)" }}>·</span>
                  <span className="flex items-center gap-1" data-testid="visite-visiteur"><Users className="w-3.5 h-3.5" style={{ color: dossier.visiteur_amo ? "#10b981" : "var(--oh-text-muted)" }} />{dossier.visiteur_amo || "Visiteur non assigné"}</span>
                  <span style={{ color: "var(--oh-text-muted)" }}>· statut RDV :</span>
                  {verrouille ? (
                    <Badge className="text-xs border-0" style={{ background: `${visiteColor}20`, color: visiteColor }} data-testid="visite-statut">{visiteLabel}</Badge>
                  ) : (
                    <select
                      value={radioVisiteValue}
                      onChange={(e) => !savingStatut && changeStatutVisite(e.target.value)}
                      disabled={savingStatut}
                      className="rounded-md px-2 py-1 text-xs"
                      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: visiteColor }}
                      data-testid="visite-statut-select"
                      aria-label="Statut RDV"
                    >
                      <option value="non_planifie">Non planifiée</option>
                      <option value="planifie">Planifiée</option>
                      <option value="effectue">Effectuée</option>
                    </select>
                  )}
                </div>

                {/* Ligne secondaire : contact + adresse + GCal link (visible uniquement si date planifiée) */}
                {dossier.date_visite && (
                  <div className="rounded-lg p-3 space-y-1.5" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
                    {adresseVisite && (
                      <div className="flex items-start gap-2 text-xs" style={{ color: "var(--oh-text)" }} data-testid="visite-adresse">
                        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--oh-text-muted)", minWidth: "70px" }}>Adresse</span>
                        <a
                          href={adresseMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] hover:underline"
                          data-testid="visite-adresse-maps-link"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span>{adresseVisite}{adresseComplement ? ` (${adresseComplement})` : ""}</span>
                        </a>
                      </div>
                    )}
                    {(clientTel || clientMobile || clientEmail) && (
                      <div className="flex items-start gap-2 text-xs flex-wrap" style={{ color: "var(--oh-text)" }} data-testid="visite-contact-client">
                        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--oh-text-muted)", minWidth: "70px" }}>Client</span>
                        <div className="flex gap-3 flex-wrap">
                          {clientTel && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {clientTel}</span>}
                          {clientMobile && clientMobile !== clientTel && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-[var(--oh-accent-text)]" /> {clientMobile}</span>}
                          {clientEmail && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {clientEmail}</span>}
                        </div>
                      </div>
                    )}
                    {dossier.gcal_visite_event_url && (
                      <div className="flex items-start gap-2 text-xs" data-testid="visite-gcal-link">
                        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--oh-text-muted)", minWidth: "70px" }}>Calendar</span>
                        <a href={dossier.gcal_visite_event_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] hover:underline">
                          <ExternalLink className="w-3 h-3" /> Ouvrir dans Google Calendar
                        </a>
                      </div>
                    )}
                    {dossier.visite_description && (
                      <div className="flex items-start gap-2 text-xs" style={{ color: "var(--oh-text)" }} data-testid="visite-description">
                        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--oh-text-muted)", minWidth: "70px" }}>Description</span>
                        <span className="whitespace-pre-wrap">{dossier.visite_description}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Paiement visite (au visiteur) — LECTURE SEULE ici. Le toggle
                    éditable est dans l'onglet Finance (mockup Demande MPA, règle 4). */}
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: dossier.visite_payee ? "rgba(16,185,129,0.06)" : "var(--oh-bg-surface)", border: `1px solid ${dossier.visite_payee ? "rgba(16,185,129,0.2)" : "var(--oh-border)"}` }} data-testid="visite-paiement-readonly">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4" style={{ color: dossier.visite_payee ? "#10b981" : "var(--oh-text-muted)" }} />
                    <span className="text-sm" style={{ color: "var(--oh-text)" }}>Paiement visite (au visiteur)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-xs border-0" style={{ background: dossier.visite_payee ? "#10b98120" : "var(--oh-stat-amber-bg)", color: dossier.visite_payee ? "#10b981" : "var(--oh-stat-amber-text)" }} data-testid="visite-paiement-badge">
                      {dossier.visite_payee ? "Payée" : "Non payée"}
                    </Badge>
                    <span className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>éditable dans Finance</span>
                  </div>
                </div>
            </div>
            );
          })()}
        </BlockCard>
        {/* CC-DEV1-17 #2 — Bloc DAL Mobile déplacé vers l'onglet DAL (1ère section),
            plus cohérent. Rendu dans DossierDetail (onglet "dal"). */}
      </div>
      )}

      {/* ─── 3. FINANCEMENT PROJET (lecture seule si montants existants) ─── */}
      {(dossier.montant_devis || dossier.montant_aides || dossier.montant_prime || dossier.reste_a_charge) && (
      <div data-subnav-id="financement">
        <BlockCard collapsible marginBottom={0} iconColor="var(--oh-hub-operation)" testId="bloc-financement" title="Financement projet" icon={Banknote}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <ReadField label="Montant travaux" value={fmtEuro(dossier.montant_devis)} />
            <ReadField label="Aides" value={fmtEuro(dossier.montant_aides || dossier.montant_prime)} color="#10b981" />
            <ReadField label="Reste à charge" value={fmtEuro(dossier.reste_a_charge)} color="var(--oh-stat-red-text)" />
          </div>
        </BlockCard>
      </div>
      )}

      {/* ─── 6. PHOTOS + CHANTIER (children) ─── */}
      {children}

      {/* ─── Modal Planifier / Modifier Visite ─── */}
      <Dialog open={showPlanifModal} onOpenChange={setShowPlanifModal}>
        <DialogContent style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", maxWidth: "520px" }} data-testid="modal-planifier-visite">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--oh-text)" }} className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-[var(--oh-accent-text)]" />
              {planifMode === "edit" ? "Modifier la visite AMO" : "Planifier la visite AMO"}
            </DialogTitle>
          </DialogHeader>

          {planifMode === "edit" && (
            <div className="rounded-lg p-3 text-xs flex items-start gap-2" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid var(--oh-stat-amber-active-bg)", color: "var(--oh-stat-amber-text)" }} data-testid="warn-edit-visite">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Les changements seront répercutés sur Google Calendar. Si la date ou l'heure change, le client recevra un SMS de mise à jour.</span>
            </div>
          )}

          <div className="space-y-4 py-2">
            {/* Titre (pré-rempli) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Titre</label>
              <Input
                type="text"
                value={planifForm.titre}
                onChange={(e) => setPlanifForm(f => ({ ...f, titre: e.target.value }))}
                placeholder="Visite — Nom Client"
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="planif-titre"
              />
            </div>

            {/* Visiteur */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Visiteur AMO</label>
              <select
                value={planifForm.visiteur_id || ""}
                onChange={(e) => handleSelectVisiteur(e.target.value)}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="planif-visiteur-select"
              >
                <option value="">Sélectionner un visiteur</option>
                {visiteursList.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
              {planifForm.visiteur_nom && (
                <div className="flex items-center gap-3 text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
                  {planifForm.visiteur_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {planifForm.visiteur_email}</span>}
                  {planifForm.visiteur_telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {planifForm.visiteur_telephone}</span>}
                </div>
              )}
            </div>

            {/* Date + Heures */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Date</label>
                <Input
                  type="date"
                  value={planifForm.date}
                  onChange={(e) => setPlanifForm(f => ({ ...f, date: e.target.value }))}
                  style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                  data-testid="planif-date"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Début</label>
                <Input
                  type="time"
                  value={planifForm.heure_debut}
                  onChange={(e) => setPlanifForm(f => ({ ...f, heure_debut: e.target.value }))}
                  style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                  data-testid="planif-heure-debut"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Fin</label>
                <Input
                  type="time"
                  value={planifForm.heure_fin}
                  onChange={(e) => setPlanifForm(f => ({ ...f, heure_fin: e.target.value }))}
                  style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                  data-testid="planif-heure-fin"
                />
              </div>
            </div>

            {/* Adresse logement (lieu du RDV) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Adresse du logement (lieu du RDV)</label>
              <Input
                type="text"
                value={planifForm.adresse}
                onChange={(e) => setPlanifForm(f => ({ ...f, adresse: e.target.value }))}
                placeholder="12 rue de la Paix, 75001 Paris"
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="planif-adresse"
              />
            </div>

            {/* Description / notes */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>Description / notes (optionnel)</label>
              <textarea
                value={planifForm.description}
                onChange={(e) => setPlanifForm(f => ({ ...f, description: e.target.value }))}
                rows={3}
                placeholder="Points à vérifier, accès, code, etc."
                className="w-full rounded-md px-3 py-2 text-sm resize-none"
                style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                data-testid="planif-description"
              />
            </div>

            {/* Invités (client + visiteur) */}
            <div className="rounded-lg p-3 space-y-1.5" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
              <p className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>Invités de l'événement</p>
              <div className="text-xs space-y-1" style={{ color: "var(--oh-text-muted)" }}>
                {planifForm.visiteur_email && (
                  <div className="flex items-center gap-2" data-testid="planif-invite-visiteur">
                    <Mail className="w-3 h-3" /> <span>{planifForm.visiteur_nom}</span> <span className="opacity-70">({planifForm.visiteur_email})</span>
                  </div>
                )}
                {planifForm.client_email && (
                  <div className="flex items-center gap-2" data-testid="planif-invite-client">
                    <Mail className="w-3 h-3" /> <span>Client</span> <span className="opacity-70">({planifForm.client_email})</span>
                  </div>
                )}
                {!planifForm.client_email && (
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase">Email du client (optionnel)</label>
                    <Input
                      type="email"
                      value={planifForm.client_email}
                      onChange={(e) => setPlanifForm(f => ({ ...f, client_email: e.target.value }))}
                      placeholder="client@exemple.fr"
                      style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                      data-testid="planif-client-email"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Notifications */}
            <div className="rounded-lg p-3" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
              <p className="text-xs font-medium mb-2" style={{ color: "var(--oh-text)" }}>Notifications visiteur</p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={planifForm.notify_email}
                    onCheckedChange={(v) => setPlanifForm(f => ({ ...f, notify_email: !!v }))}
                    data-testid="planif-notify-email"
                  />
                  <span className="text-xs flex items-center gap-1" style={{ color: "var(--oh-text-muted)" }}><Mail className="w-3 h-3" /> Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={planifForm.notify_sms}
                    onCheckedChange={(v) => setPlanifForm(f => ({ ...f, notify_sms: !!v }))}
                    data-testid="planif-notify-sms"
                  />
                  <span className="text-xs flex items-center gap-1" style={{ color: "var(--oh-text-muted)" }}><Phone className="w-3 h-3" /> SMS</span>
                </label>
              </div>
              <p className="text-[10px] mt-2" style={{ color: "var(--oh-text-muted)" }}>Le client recevra toujours un SMS de confirmation.</p>
            </div>

            {/* Résumé */}
            {planifForm.visiteur_nom && planifForm.date && (
              <div className="rounded-lg p-3 bg-[var(--oh-accent-bg)]" style={{ border: "1px solid rgba(59,130,246,0.2)" }}>
                <p className="text-xs text-[var(--oh-accent-text)]">
                  <strong>Résumé :</strong> Visite le {formatDate(planifForm.date)} de {planifForm.heure_debut} à {planifForm.heure_fin} avec {planifForm.visiteur_nom}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPlanifModal(false)} disabled={planifLoading}>Annuler</Button>
            <Button
              onClick={handlePlanifierVisite}
              disabled={planifLoading || !planifForm.visiteur_nom || !planifForm.date}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
              data-testid="confirm-planifier-visite"
            >
              {planifLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              {planifMode === "edit" ? "Confirmer la modification" : "Planifier et notifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
